import React, { ChangeEvent, useRef, useState } from "react";

import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  TextField,
  Autocomplete,
  Button,
  ListItemAvatar,
  Avatar,
  ListItemSecondaryAction,
  IconButton,
  ButtonProps,
} from "@mui/material";
import {
  ClearAllTwoTone,
  CloudUpload,
  Delete,
  Folder,
  Send,
} from "@mui/icons-material";
import { styled } from "@mui/system";
import { Divider } from "@material-ui/core";

import { useDrawerReporteOpen } from "store/user-settings/user-settings-store";

import { useTranslation } from "react-i18next";
import { Close } from "@mui/icons-material";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "react-toastify";

import { useGetTicketPickList } from "services/input-core/ticket/get-ticket-pick-list";
import { checkMessageErrors, formatBytes } from "utils/functions";
import { useSendTicket } from "services/input-core/ticket/send-ticket";
import { GenericInformationModal } from "components/Modals/GenericInformationModal";

const DrawerContent = styled(Box)`
  padding: 20px;
  width: 400px;
  height: 100%;

  overflow: auto;

  scrollbar-width: thin;
  scrollbar-color: #c5c5c5 transparent;

  /* Works on Chrome, Edge, and Safari */
  *::-webkit-scrollbar {
    width: 10px;
  }

  *::-webkit-scrollbar-track {
    background: transparent;
  }

  *::-webkit-scrollbar-thumb {
    padding: 4px;
    background-color: #c5c5c5;
    background-size: contain;
    border-radius: 20px;
    border: 3px solid transparent;
  }
`;

const DrawerHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const Title = styled(Typography)`
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0;
`;

const SubTitle = styled(Typography)`
  font-size: 1.2rem;
  margin-bottom: 10px;
`;

const Description = styled(Typography)`
  font-size: 0.9rem;
  opacity: 0.65;
`;

interface IPropsButtonInputFile extends ButtonProps {
  limit: () => number;
}

const ButtonInputFile = styled((props: IPropsButtonInputFile) => (
  <Button {...props} component="span" />
))<IPropsButtonInputFile>`
  background-image: ${({ limit }) =>
    `linear-gradient(to right, #f8cfa7 ${limit()}%, transparent 0%)`};
`;

type NewCallNames = "subject" | "description" | "priority" | "files";

type Modal = {
  open?: boolean;
  modalTitle: string;
  modalDescription: string;
  modalType: "confirm-action" | "sensitive-action";
  onAction: () => void;
};

type ILevel = "Baixa" | "Normal" | "Alta" | "Urgente";

enum LevelTicket {
  Baixa = "low",
  Normal = "normal",
  Alta = "high",
  Urgente = "urgent",
}

export const CustomDrawerReport = () => {
  const { t } = useTranslation([
    "side-drawer",
    "modals",
    "list-messages",
    "list-errors",
  ]);
  const { drawerReporteOpen, setDrawerReporteOpen } = useDrawerReporteOpen();

  const {
    data: dataPickList,
    isLoading: isLoadingPickList,
  } = useGetTicketPickList({
    enabled: drawerReporteOpen,
  });

  const {
    mutate: sendTicket,
    isLoading: isLoadingSendTicket,
  } = useSendTicket();

  const [modals, setModals] = useState<Modal>({
    open: false,
    modalTitle: "",
    modalDescription: "",
    modalType: "confirm-action",
    onAction: null,
  });

  const closeModal = () => {
    setModals(() => ({
      open: false,
      modalTitle: "",
      modalDescription: "",
      modalType: "confirm-action",
      onAction: null,
    }));
  };

  const openModal = (data: Modal) => {
    setModals({
      open: true,
      ...data,
    });
  };

  const fileSchema = z.instanceof(File);

  const filesSchema = z
    .array(fileSchema)
    .optional()
    .refine(
      (files) => {
        const totalSize =
          files?.reduce((acc, file) => acc + file?.size, 0) ?? 0;
        return totalSize <= 5 * 1024 * 1024; // Limite de 5MB
      },
      {
        message: t("file-size-limit"),
      },
    );

  const newCallSchema = z.object({
    subject: z.string({
      required_error: t("field-required"),
    }),
    description: z.string({
      required_error: t("field-required"),
    }),
    priority: z.string({
      required_error: t("field-required"),
    }),
    files: filesSchema,
  });

  type NewCallFormFields = z.infer<typeof newCallSchema>;

  const {
    handleSubmit,
    register,
    control,
    setValue,
    reset,
    watch,
    trigger,
    clearErrors,
    formState: { errors },
  } = useForm<NewCallFormFields>({
    mode: "all",
    criteriaMode: "all",
    resolver: zodResolver(newCallSchema),
  });

  const verificarErrosFiles = async () => {
    const isValid = await trigger("files");

    if (isValid) {
      clearErrors("files");
    }
  };

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const inputFileRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: any,
  ) => {
    const newFiles = event.target.files ? Array.from(event.target.files) : [];
    const updatedFiles = [...selectedFiles];
    newFiles.forEach((newFile) => {
      const existingFileIndex = updatedFiles.findIndex(
        (file) => file.name === newFile.name,
      );
      if (existingFileIndex !== -1) {
        // Substitui o arquivo antigo pelo novo
        updatedFiles[existingFileIndex] = newFile;
      } else {
        updatedFiles.push(newFile);
      }
    });
    setSelectedFiles(updatedFiles);
    field.onChange(updatedFiles);
  };

  const handleFileRemove = (fileName: string, inputName: NewCallNames) => {
    const updatedFiles = selectedFiles.filter((file) => file.name !== fileName);
    setSelectedFiles(updatedFiles);
    setValue(inputName, updatedFiles as [File, ...File[]]);
    verificarErrosFiles();
    if (inputFileRef.current) {
      inputFileRef.current.value = ""; // Limpa o valor do input
    }
  };

  const renderTextField = (
    name: NewCallNames,
    label: string,
    required = true,
    type = "text",
    multiline?: boolean,
    rows?: number,
    maxRows?: number,
  ) => (
    <Box display="flex" flexDirection="column" width="100%">
      <Controller
        // data-cy={`controller-${name}`}
        name={name}
        control={control}
        defaultValue=""
        render={({ field }) => (
          <TextField
            {...field}
            // data-cy={`input-${name}`}
            id={name}
            variant="outlined"
            label={t(label)}
            required={required}
            type={type}
            style={{ width: "100%" }}
            multiline={multiline}
            rows={rows}
            defaultValue=""
            maxRows={maxRows}
            InputLabelProps={type === "date" ? { shrink: true } : {}}
            {...register(name, { required: true })}
          />
        )}
      />
      {errors[name] && (
        <Typography color="error" variant="caption" mt={1}>
          {errors[name]?.message}
        </Typography>
      )}
    </Box>
  );

  const renderAutocompleteField = (
    name: NewCallNames,
    label: string,
    options: { value: string | string; label: string }[],
    isMultiple = false,
  ) => (
    <Box display="flex" flexDirection="column" width="100%">
      <Controller
        // data-cy={`controller-${name}`}
        name={name}
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <Autocomplete
            {...field}
            // data-cy={`autocomplete-${name}`}
            id={name}
            key={field?.value?.toString() || name}
            limitTags={4}
            multiple={isMultiple}
            options={options}
            getOptionLabel={(option: { value: string; label: string }) =>
              t(option?.label)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                data-cy={`input-${name}`}
                label={t(label)}
                error={Boolean(errors[name])}
                helperText={errors[name]?.message}
              />
            )}
            onChange={(_, newValue: any) => {
              if (!newValue) {
                setValue(name, undefined);
              } else if (isMultiple) {
                const selectedValues = newValue.map(
                  (option: { value: string; label: string }) => option.value,
                );
                field.onChange(selectedValues);
                setValue(name, selectedValues);
              } else {
                field.onChange(newValue.value);
                setValue(name, newValue.value);
              }
            }}
            value={
              isMultiple
                ? (field?.value as Array<any>)?.map(
                    (value) =>
                      options.find((option) => option.value === value) ||
                      undefined,
                  )
                : options?.find((option) => option.value === field.value) ||
                  undefined
            }
          />
        )}
      />
    </Box>
  );

  const renderInputFile = (name: NewCallNames, label: string) => (
    <Box display="flex" flexDirection="column" width="100%">
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <>
            <input
              accept=".pdf, .png, .jpg, .jpeg, .gif"
              id={`contained-button-file-${name}`}
              multiple
              type="file"
              style={{ display: "none" }}
              ref={inputFileRef}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                handleFileChange(event, field);
              }}
            />
            <label
              htmlFor={`contained-button-file-${name}`}
              style={{ width: "100%" }}
            >
              <ButtonInputFile
                component="span"
                variant="outlined"
                color="warning"
                startIcon={<CloudUpload />}
                size="large"
                limit={() => {
                  const totalSize =
                    watch("files")?.reduce(
                      (acc, file) => acc + file?.size,
                      0,
                    ) ?? 0;

                  // Calcular a porcentagem do limite
                  const limit = 5 * 1024 * 1024; // 5MB em bytes
                  const percentage = (totalSize / limit) * 100;

                  return percentage;
                }}
                fullWidth
              >
                {label}
              </ButtonInputFile>
            </label>
            {errors[name] && (
              <Typography color="error" variant="caption" mt={1}>
                {errors[name]?.message}
              </Typography>
            )}
          </>
        )}
      />
    </Box>
  );

  const isFormValid = () => {
    const maxTotalSize = 5 * 1024 * 1024;
    const totalSize = selectedFiles.reduce((acc, file) => acc + file.size, 0);

    const isFilesValid = totalSize >= 0 && totalSize <= maxTotalSize;
    const isFieldsFilled =
      watch("subject") && watch("description") && watch("priority");

    return isFilesValid && isFieldsFilled;
  };

  const onSubmit = async (data: NewCallFormFields) => {
    const formData = new FormData();

    // Adicionando os campos ao formData
    formData.append("priority", data.priority);
    formData.append("subject", data.subject);
    formData.append("description", data.description);

    // Adicionando os arquivos
    data?.files?.forEach((file) => {
      formData.append("files", file);
    });

    openModal({
      modalTitle: t("open-ticket", { ns: "modals" }),
      modalDescription: t("confirm-open-ticket", { ns: "modals" }),
      modalType: "confirm-action",
      onAction: () => {
        toast.info(t("info-sending-call", { ns: "list-messages" }));
        closeModal();
        sendTicket(formData, {
          onSuccess: () => {
            toast.success(
              t("success-create-opening-calls", {
                ns: "list-messages",
              }),
            );
            handleReset();
            setDrawerReporteOpen(false);
          },
          onError: (error: any) => {
            toast.error(
              t(
                checkMessageErrors(
                  error?.response?.data?.error,
                  "ERR_TICKET_DEFAULT",
                ),
                { ns: "list-errors" },
              ),
            );
            closeModal();
          },
        });
      },
    });
  };

  const handleReset = () => {
    reset();
    setSelectedFiles([]);
  };

  return (
    <>
      {modals.open && (
        <GenericInformationModal
          modalOpen={modals.open}
          modalTitle={modals.modalTitle}
          modalDescription={modals.modalDescription}
          modalType={modals.modalType}
          onClose={() => {
            closeModal();
          }}
          onAction={modals.onAction}
        />
      )}
      <Drawer
        variant="temporary"
        anchor="right"
        open={drawerReporteOpen}
        // onClose={() => setDrawerReporteOpen(false)}
        transitionDuration={{
          enter: 300,
          exit: 300,
        }}
      >
        <DrawerContent>
          <DrawerHeader>
            <Title variant="h1" gutterBottom>
              {t("opening-calls")}
            </Title>
            <Close
              style={{
                cursor: "pointer",
                fontSize: "1.5rem",
              }}
              onClick={() => {
                handleReset();
                setDrawerReporteOpen(false);
              }}
            />
          </DrawerHeader>

          <Divider style={{ backgroundColor: "#808080" }} />
          <form
            onSubmit={handleSubmit(onSubmit)}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "space-between",
              height: "calc(100% - 53px)",
            }}
          >
            <Box width={"100%"}>
              <List>
                <ListItem>
                  {renderAutocompleteField(
                    "priority",
                    "priority",
                    dataPickList?.picklistValues?.map(
                      (option: { id: string; value: ILevel }) => ({
                        value: option.id,
                        label: LevelTicket[option.value],
                      }),
                    ),
                  )}
                </ListItem>
                <ListItem>
                  {renderTextField("subject", "subject", true)}
                </ListItem>
                <ListItem>
                  {renderTextField(
                    "description",
                    "description",
                    true,
                    "text",
                    true,
                    12,
                    12,
                  )}
                </ListItem>
                <ListItem>
                  <ListItemText>
                    <SubTitle variant="caption" gutterBottom>
                      {t("attach-documents")}
                    </SubTitle>
                    <Description variant="body2">{t("size-limit")}</Description>
                  </ListItemText>
                </ListItem>
              </List>

              <List dense>
                {selectedFiles?.map((file, index) => (
                  <ListItem key={index}>
                    <ListItemAvatar>
                      <Avatar>
                        <Folder />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={file?.name}
                      secondary={`${file?.type} - ${formatBytes(file?.size)}`}
                      sx={{
                        ".MuiListItemText-primary": {
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        },
                      }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleFileRemove(file?.name, "files")}
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
                <ListItem>
                  <ListItemText>
                    {renderInputFile("files", t("add-new-attachment"))}
                  </ListItemText>
                </ListItem>
              </List>
            </Box>

            <Box width={"100%"}>
              <Divider style={{ backgroundColor: "#808080" }} />

              <List>
                <ListItem>
                  <ListItemText>
                    <Box
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"space-between"}
                    >
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<ClearAllTwoTone />}
                        size="large"
                        onClick={() =>
                          openModal({
                            modalTitle: t("clear-form-fields", {
                              ns: "modals",
                            }),
                            modalDescription: t("confirm-clear-fields", {
                              ns: "modals",
                            }),
                            modalType: "sensitive-action",
                            onAction: () => {
                              handleReset();
                              closeModal();
                            },
                          })
                        }
                      >
                        {t("clear")}
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        endIcon={<Send />}
                        size="large"
                        type="submit"
                        disabled={
                          isLoadingPickList ||
                          isLoadingSendTicket ||
                          !isFormValid()
                        }
                      >
                        {t("send")}
                      </Button>
                    </Box>
                  </ListItemText>
                </ListItem>
              </List>
            </Box>
          </form>
        </DrawerContent>
      </Drawer>
    </>
  );
};
